import { Card, csx, cx } from '@vtex/admin-ui'
import type { Ref } from 'react'
import React, { forwardRef, useRef } from 'react'
import { useDrag } from 'react-dnd'

import { useForkRef } from '../../../../core/hooks/useForkRef'
import type { DragData } from '../../shared/types'

export function getContainerStyles(
  isDragging: boolean,
  usedAsDragPreview: boolean
) {
  return {
    cursor: 'pointer',
    // [TODO] <theme>: create opacity variables on theme?
    borderRadius: 'default',
    opacity: isDragging ? 0.5 : 1,
    border: usedAsDragPreview ? '$mainSelected' : '$neutral',
    borderWidth: usedAsDragPreview ? '2px' : '1px',
    backgroundColor: usedAsDragPreview
      ? '$action.main.tertiaryPressed'
      : '$white',
    transitionProperty: 'border, background-color, border-style, opacity',
    transitionDuration: '0.3s',
    transitionTimingFunction: 'ease-out',
  }
}

type Props<ItemType> = {
  ref: React.ForwardedRef<HTMLDivElement>
  item: ItemType
  index: number
  dndType: string
  children: React.ReactNode | React.ReactNode[]
  'data-testid'?: string
  onClick?: () => void
  usedAsDragPreview?: boolean
  className?: string
}

// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// eslint-disable-next-line react/display-name
const DraggableCard = forwardRef(
  <ItemType,>(
    {
      item,
      dndType,
      index,
      children,
      className = '',
      'data-testid': dataTestId,
      onClick,
      usedAsDragPreview = false,
    }: Props<ItemType>,
    ref: Ref<HTMLDivElement>
  ) => {
    const internalRef = useRef<HTMLDivElement>(null)

    const [{ isDragging }, drag] = useDrag<
      DragData<ItemType>,
      unknown,
      { isDragging: boolean }
    >({
      type: dndType,
      item: () => ({
        queueItem: item,
        element: internalRef.current!,
        index,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    })

    drag(internalRef)

    return (
      <Card
        onClick={onClick}
        ref={useForkRef(internalRef, ref)}
        className={cx(
          csx({
            ...getContainerStyles(isDragging, usedAsDragPreview),
          }),
          className
        )}
        data-testid={dataTestId}
      >
        {children}
      </Card>
    )
  }
)

export default DraggableCard
